import { useEffect, useRef, useState } from "react"
import { AntdButton, AntdSelect1, LocationSidebar } from "components"
import { useAppDispatch, useAppSelector, useHeightResize } from "hooks"
import { CaretDownOutlined } from "@ant-design/icons"
import {
	getLocationContractsByLocationId,
	isLocationContractsLoading,
	selectLocationContractsDataDropDown,
} from "store/Location/locationContracts.slice"
import RotaPlannerModal from "./RotaPlannerModal"
import { clearLocationPosts, getLocationPostsByContractId } from "store/Location/locationPosts.slice"
import {
	clearRotaPlannerState,
	fetchRotaPlan,
	isRotaPlannerLoading,
	selectRotaPlannerData,
	updateRotaPlanTimes,
} from "store/RotaPlanner/rotaPlanner.slice"
import RotaPlannerTable from "./RotaPlannerTable"
import { UpdateRotaPlan } from "@type/rotaPlanner.types"
import DuplicateContractModal from "pages/locations/Contracts/DuplicateContractModal"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import { getAllActiveEmployees } from "store/Employee/detail.slice"
import { useLocationScheduleDefaults } from "hooks/useScheduleDefaults"

const RotaPlanner = () => {
	useHeightResize({ element: "planner-container" })
	useLocationScheduleDefaults()
	const updateRowRef = useRef<UpdateRotaPlan[]>([])
	const dispatch = useAppDispatch()
	const { selectedLocation = "", contract: paramContract, selectLocation, selectContract } = useSchedulesQueryParams()

	//Contract Selector
	const contractsList = useAppSelector(selectLocationContractsDataDropDown(selectedLocation))
	const isContractsLoading = useAppSelector(isLocationContractsLoading())

	//Rota Plan Selector
	const rotaPlan = useAppSelector(selectRotaPlannerData)
	const isRotaPlanLoading = useAppSelector(isRotaPlannerLoading())

	// Modal State
	const [showRotaPlannerModal, setShowRotaPlannerModal] = useState(false)
	const [showDuplicateContractModal, setShowDuplicateContractModal] = useState<boolean>(false)

	useEffect(() => {
		dispatch(getAllActiveEmployees(true))
	}, [])

	useEffect(() => {
		if (selectedLocation) {
			dispatch(getLocationContractsByLocationId(selectedLocation))
			dispatch(clearRotaPlannerState())
		}
		selectContract(contractsList?.[1]?.value)
	}, [selectedLocation])

	useEffect(() => {
		if (paramContract) {
			dispatch(fetchRotaPlan(paramContract))
		} else if (
			contractsList.length &&
			(!paramContract || !contractsList.find(contract => contract.value === paramContract))
		) {
			selectContract(contractsList?.[1]?.value)
		}
	}, [paramContract])

	const handleSubmit = () => {
		if (paramContract) {
			dispatch(updateRotaPlanTimes(paramContract, updateRowRef.current, () => (updateRowRef.current = [])))
		}
	}

	return (
		<div id="planner-container" className="h-full">
			<LocationSidebar
				selected={selectedLocation}
				handleSelected={locationId => {
					selectLocation(locationId)
				}}
			/>
			<div className="flex w-1/2 space-x-4 py-3">
				<AntdSelect1
					allowClear
					disabled={!contractsList.length || !selectedLocation}
					onChange={event => {
						selectContract(event)
						event ? dispatch(getLocationPostsByContractId(event)) : dispatch(clearLocationPosts())
					}}
					value={paramContract}
					loading={isContractsLoading}
					placeholder="Select Contract"
					options={contractsList || []}
					suffixIcon={
						<>
							<span style={{ color: "rgb(156 163 175)" }}>Contract</span>
							<CaretDownOutlined style={{ color: "rgb(156 163 175)" }} />
						</>
					}
				/>
				{!rotaPlan?._id && (
					<AntdButton onClick={() => setShowRotaPlannerModal(prev => !prev)}>Create Plan</AntdButton>
				)}
				<AntdButton onClick={() => setShowDuplicateContractModal(prev => !prev)}>Duplicate Contract</AntdButton>
				{paramContract && (
					<AntdButton onClick={handleSubmit} loading={isRotaPlanLoading} disabled={isRotaPlanLoading}>
						Save
					</AntdButton>
				)}
			</div>
			<RotaPlannerTable ref={updateRowRef} />
			{showRotaPlannerModal && (
				<RotaPlannerModal
					open={showRotaPlannerModal}
					modalHandler={setShowRotaPlannerModal}
					contractId={paramContract || ""}
				/>
			)}
			{showDuplicateContractModal && (
				<DuplicateContractModal
					open={showDuplicateContractModal}
					modalHandler={setShowDuplicateContractModal}
				/>
			)}
		</div>
	)
}

export default RotaPlanner
